import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BufferCalculationType } from 'business/BufferCalculationType';
import {
  IIngredientDocAliasDTO,
  IIngredientDocBufferDataPoint,
  IIngredientDocCasNumberDTO,
  IIngredientDocInfo,
  IIngredientDocStockDTO,
  IIngredientTypeDTO,
  IIngredientDocDeletedData,
  State,
  IngredientDocumentDTO,
} from 'business/models/Models';
import {
  getIngredientById,
  getStocksByIngredientId,
} from 'business/services/IngredientService';
import { getIngredientDocPlainData } from 'transformers/IngredientDetails';
import { RowExtraInfo } from 'components/customComponents/Controls/RMTable';
import { cloneDeep } from 'lodash';

type IngredientDetailState = {
  current: IngredientDocumentData;
  original?: IngredientDocumentData;
  selectedStockRows: number[];
  deleted: IIngredientDocDeletedData;
  loading: boolean;
  errorVisiblity: boolean;
  stockErrors: RowExtraInfo;
  aliasErrors: RowExtraInfo;
  casNumberErrors: RowExtraInfo;
  isDirty: boolean;
  bufferDataErrors: RowExtraInfo;
  mergedAndDeletedStockIds: { [key: string]: number };
};

export type IngredientDocumentData = {
  info: IIngredientDocInfo;
  types: IIngredientTypeDTO[];
  aliases: IIngredientDocAliasDTO[];
  casNumbers: IIngredientDocCasNumberDTO[];
  stocks: IIngredientDocStockDTO[];
  bufferDataPoint: IIngredientDocBufferDataPoint[];
};

type PayloadTuple = {
  key: string;
  value: any;
};

type ListElementUpdateDTO = {
  key: string;
  value: any;
  id: number;
};
const defaultIngredientData = {
  info: {
    longName: '',
    shortName: '',
    id: 0,
    bufferCalculationTypeId: BufferCalculationType.pKa.ID,
    pKa: 7,
    isUsed: false,
  } as IIngredientDocInfo,
  types: [],
  aliases: [],
  casNumbers: [],
  stocks: [],
  bufferDataPoint: [],
};

const defaultDeletedState = {
  types: [],
  stocks: [],
  casNumbers: [],
  aliases: [],
  bufferDataPoints: [],
};
const initialState: IngredientDetailState = {
  current: defaultIngredientData,
  original: { ...defaultIngredientData },
  selectedStockRows: [],
  deleted: defaultDeletedState,
  loading: true,
  errorVisiblity: false,
  stockErrors: {},
  casNumberErrors: {},
  aliasErrors: {},
  isDirty: false,
  mergedAndDeletedStockIds: {},
  bufferDataErrors: {},
};

const fetchIngredientDocumentData = async (id: number) => {
  let res: IngredientDocumentDTO;
  let stocks = [];
  const fetchIngredient = async () => {
    res = await getIngredientById(id);
  };
  const fetchIngredientStocks = async () => {
    stocks = await getStocksByIngredientId(id);
  };
  await Promise.all([fetchIngredient(), fetchIngredientStocks()]);
  res.stocks = stocks;
  res.info.isUsed = res.info.isUsed || stocks.some((stock) => stock.isUsed);
  return getIngredientDocPlainData(res);
};

export const getIngredientData = createAsyncThunk(
  'ingredientDetail/get',
  async (id: number) => {
    if (id > 0) {
      return fetchIngredientDocumentData(id);
    }
    return defaultIngredientData;
  },
);

const ingredientDetailSlice = createSlice({
  name: 'ingredientDetail',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(getIngredientData.fulfilled, (state, action) => {
      state.current = action.payload;
      state.original = cloneDeep(action.payload);
      state.loading = false;
      state.errorVisiblity = false;
      state.stockErrors = {};
      state.deleted = defaultDeletedState;
      state.bufferDataErrors = {};
      state.casNumberErrors = {};
      state.aliasErrors = {};
      state.isDirty = false;
    });
  },
  reducers: {
    setIngredientInfoByKey: (state, action: PayloadAction<PayloadTuple>) => {
      state.current.info = {
        ...state.current.info,
        [action.payload.key]: action.payload.value,
      };
      state.current.info.state = state.current.info.id > 0 ? State.Updated : State.New;
      state.isDirty = true;
    },
    addTypes: (state, action: PayloadAction<IIngredientTypeDTO[]>): void => {
      state.current.types = state.current.types.concat(action.payload);
      if (action.payload.find((t) => t.isBuffer)) {
        if (!state.current.info.bufferCalculationTypeId) {
          state.current.info.bufferCalculationTypeId = BufferCalculationType.pKa.ID;
        }
        if (!state.current.info.pKa) {
          state.current.info.pKa = 7;
        }
      }
      state.isDirty = true;
    },
    removeTypes: (state, action: PayloadAction<IIngredientTypeDTO[]>): void => {
      state.current.types = state.current.types.filter(
        (t) => !action.payload.find((type) => type.id === t.id),
      );
      const deletedTypes = action.payload.map((t) => t.id);
      state.deleted.types = [...state.deleted.types, ...deletedTypes];
      state.isDirty = true;
    },
    addAlias: (state, action: PayloadAction<IIngredientDocAliasDTO>): void => {
      state.current.aliases.push(action.payload);
      state.isDirty = true;
    },
    removeAliases: (state, action: PayloadAction<IIngredientDocAliasDTO[]>): void => {
      state.current.aliases = state.current.aliases.filter(
        (a) => !action.payload.find((alias) => alias.id === a.id),
      );
      action.payload.forEach((a) => {
        if (a.id > 0) {
          state.deleted.aliases.push(a.id);
        }
      });
      state.isDirty = true;
    },
    updateAliasById: (state, action: PayloadAction<IIngredientDocAliasDTO>): void => {
      const alias = state.current.aliases.find((a) => a.id === action.payload.id);
      if (alias) {
        alias.name = action.payload.name;
        if (alias.id > 0) {
          alias.state = State.Updated;
        }
      }
      state.isDirty = true;
    },
    addCASNumber: (state, action: PayloadAction<IIngredientDocCasNumberDTO>): void => {
      state.current.casNumbers.push(action.payload);
      state.isDirty = true;
    },
    removeCASNumbers: (
      state,
      action: PayloadAction<IIngredientDocCasNumberDTO[]>,
    ): void => {
      state.current.casNumbers = state.current.casNumbers.filter(
        (c) => !action.payload.find((cas) => cas.id === c.id),
      );
      action.payload.forEach((c) => {
        if (c.id > 0) {
          state.deleted.casNumbers.push(c.id);
        }
      });
      state.isDirty = true;
    },
    updateCASNumberById: (
      state,
      action: PayloadAction<IIngredientDocCasNumberDTO>,
    ): void => {
      const casNumber = state.current.casNumbers.find((c) => c.id === action.payload.id);
      if (casNumber) {
        casNumber.number = action.payload.number;
        if (casNumber.id > 0) {
          casNumber.state = State.Updated;
        }
      }
      state.isDirty = true;
    },
    addStock: (state, action: PayloadAction<IIngredientDocStockDTO>): void => {
      state.current.stocks.push(action.payload);
      state.isDirty = true;
    },
    setStocks: (state, action: PayloadAction<IIngredientDocStockDTO[]>): void => {
      state.current.stocks = action.payload.map((stock) => {
        stock.state = State.Updated;
        return stock;
      });
    },
    removeStocks: (state, action: PayloadAction<IIngredientDocStockDTO[]>): void => {
      state.current.stocks = state.current.stocks.filter(
        (c) => !action.payload.find((stock) => stock.id === c.id),
      );
      state.selectedStockRows = [];
      action.payload.forEach((s) => {
        if (s.id > 0) {
          state.deleted.stocks.push(s.id);
        }
        if (s.id === state.current.info.defaultBufferIngredientStockId) {
          state.current.info.defaultBufferIngredientStockId = null;
        }
        if (s.id === state.current.info.defaultNonBufferIngredientStockId) {
          state.current.info.defaultNonBufferIngredientStockId = null;
        }
      });
      state.isDirty = true;
    },
    updateStockPropertyById: (
      state,
      action: PayloadAction<ListElementUpdateDTO>,
    ): void => {
      const stock = state.current.stocks.find((s) => s.id === action.payload.id);
      if (stock) {
        stock[action.payload.key] = action.payload.value;
        if (stock.id > 0) {
          stock.state = State.Updated;
        }
        if (action.payload.key === 'isBuffer') {
          if (
            state.current.info.defaultBufferIngredientStockId === action.payload.id &&
            !action.payload.value
          ) {
            state.current.info.defaultBufferIngredientStockId = null;
          }
          if (
            state.current.info.defaultNonBufferIngredientStockId === action.payload.id &&
            action.payload.value
          ) {
            state.current.info.defaultNonBufferIngredientStockId = null;
          }
        }
      }
      state.isDirty = true;
    },
    updateStockById: (state, action: PayloadAction<IIngredientDocStockDTO>): void => {
      const stockIndex = state.current.stocks.findIndex(
        (s) => s.id === action.payload.id,
      );
      if (stockIndex >= 0) {
        state.current.stocks[stockIndex] = action.payload;
        state.current.stocks[stockIndex].state = State.Updated;
      }
      state.isDirty = true;
    },
    setDefaultBufferStock: (state, action: PayloadAction<number>): void => {
      state.current.info.defaultBufferIngredientStockId = action.payload;
      if (state.current.info.defaultNonBufferIngredientStockId === action.payload) {
        state.current.info.defaultNonBufferIngredientStockId = null;
      }
      if (state.current.info.id > 0) {
        state.current.info.state = State.Updated;
      }
      if (action.payload) {
        const stock = state.current.stocks.find((s) => s.id === action.payload);
        stock.isBuffer = true;
      }
      state.isDirty = true;
    },
    setDefaultNonBufferStock: (state, action: PayloadAction<number>): void => {
      state.current.info.defaultNonBufferIngredientStockId = action.payload;
      if (state.current.info.defaultBufferIngredientStockId === action.payload) {
        state.current.info.defaultBufferIngredientStockId = null;
      }
      if (state.current.info.id > 0) {
        state.current.info.state = State.Updated;
      }
      if (action.payload) {
        const stock = state.current.stocks.find((s) => s.id === action.payload);
        stock.isBuffer = false;
      }
      state.isDirty = true;
    },
    setSelectedStockRows: (state, action: PayloadAction<number[]>): void => {
      state.selectedStockRows = action.payload;
    },
    addBufferDataPoint: (
      state,
      action: PayloadAction<IIngredientDocBufferDataPoint>,
    ): void => {
      state.current.bufferDataPoint.push(action.payload);
      state.isDirty = true;
    },
    removeBufferDataPoint: (
      state,
      action: PayloadAction<IIngredientDocBufferDataPoint[]>,
    ): void => {
      state.current.bufferDataPoint = state.current.bufferDataPoint.filter(
        (bdp) => !action.payload.find((p) => p.id === bdp.id),
      );
      action.payload.forEach((p) => {
        if (p.id > 0) {
          state.deleted.bufferDataPoints.push(p.id);
        }
      });
      state.isDirty = true;
    },
    updateBufferDataPointById: (
      state,
      action: PayloadAction<ListElementUpdateDTO>,
    ): void => {
      const index = state.current.bufferDataPoint.findIndex(
        (p) => p.id === action.payload.id,
      );
      if (index >= 0) {
        state.current.bufferDataPoint[index][action.payload.key] = action.payload.value;
        if (state.current.bufferDataPoint[index].id > 0) {
          state.current.bufferDataPoint[index].state = State.Updated;
        }
      }
      state.current.bufferDataPoint = [...state.current.bufferDataPoint];
      state.isDirty = true;
    },
    setErrorVisibility: (state, action: PayloadAction<boolean>) => {
      state.errorVisiblity = action.payload;
    },
    setStockErrors: (state, action: PayloadAction<RowExtraInfo>) => {
      state.stockErrors = action.payload;
    },
    setLoadingState: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    discardChanges: (state) => {
      state.current = cloneDeep(state.original);
      state.isDirty = false;
      state.deleted = defaultDeletedState;
    },
    setDirty: (state, action: PayloadAction<boolean>) => {
      state.isDirty = action.payload;
    },
    setMergedAndDeletedStockIds: (
      state,
      action: PayloadAction<{ [key: string]: number }>,
    ): void => {
      state.mergedAndDeletedStockIds[action.payload.key] = action.payload.value;
      state.isDirty = true;
    },
    setAliasErrors: (state, action: PayloadAction<RowExtraInfo>) => {
      state.aliasErrors = action.payload;
    },
    setCasNumberErrors: (state, action: PayloadAction<RowExtraInfo>) => {
      state.casNumberErrors = action.payload;
    },
    setBufferDataErrors: (state, action: PayloadAction<RowExtraInfo>) => {
      state.bufferDataErrors = action.payload;
    },
  },
});

export const {
  setIngredientInfoByKey,
  addTypes,
  removeTypes,
  addAlias,
  removeAliases,
  updateAliasById,
  addCASNumber,
  removeCASNumbers,
  updateCASNumberById,
  addStock,
  setStocks,
  removeStocks,
  updateStockPropertyById,
  updateStockById,
  setDefaultBufferStock,
  setDefaultNonBufferStock,
  setSelectedStockRows,
  addBufferDataPoint,
  removeBufferDataPoint,
  updateBufferDataPointById,
  setErrorVisibility,
  setStockErrors,
  setLoadingState,
  discardChanges,
  setDirty,
  setMergedAndDeletedStockIds,
  setAliasErrors,
  setCasNumberErrors,
  setBufferDataErrors,
} = ingredientDetailSlice.actions;
export default ingredientDetailSlice.reducer;
